import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { interviewAPI, groupAPI } from '../services/api';

function Interviews() {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    groupId: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalInterviews: 0
  });

  useEffect(() => {
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchInterviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.search, filters.groupId, filters.startDate, filters.endDate]);

  const fetchGroups = async () => {
    try {
      const response = await groupAPI.getMyGroups();
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchInterviews = async (page = 1) => {
    setLoading(true);
    try {
      const response = await interviewAPI.getInterviews(
        page, 
        20, 
        filters.status, 
        filters.search,
        filters.groupId,
        filters.startDate,
        filters.endDate
      );
      setInterviews(response.data.interviews || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error fetching interviews:', error);
    }
    setLoading(false);
  };

  const getRemainingDays = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { text: 'Expired', color: '#e53e3e' };
    } else if (diffDays === 0) {
      return { text: 'Ending today', color: '#dd6b20' };
    } else if (diffDays === 1) {
      return { text: 'Today', color: '#dd6b20' };
    } else if (diffDays === 2) {
      return { text: 'Tomorrow', color: '#d69e2e' };
    } else if (diffDays <= 3) {
      return { text: `${diffDays} days left`, color: '#d69e2e' };
    } else {
      return { text: `${diffDays} days left`, color: '#38a169' };
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this interview?')) return;
    
    try {
      await interviewAPI.deleteInterview(id);
      alert('Interview deleted successfully');
      fetchInterviews(pagination.currentPage);
    } catch (error) {
      console.error('Error deleting interview:', error);
      alert('Failed to delete interview');
    }
  };

  const handleDuplicate = async (id) => {
    if (!window.confirm('Duplicate this interview?\n\nA copy will be created with all settings except assigned students. You will need to assign students and adjust dates.')) return;
    
    try {
      const response = await interviewAPI.duplicateInterview(id);
      alert(response.data.message || 'Interview duplicated successfully');
      fetchInterviews(pagination.currentPage);
      // Optionally navigate to edit page
      if (response.data.interview?._id) {
        navigate(`/edit-interview/${response.data.interview._id}`);
      }
    } catch (error) {
      console.error('Error duplicating interview:', error);
      alert(error.response?.data?.message || 'Failed to duplicate interview');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: { text: 'Scheduled', class: 'status-scheduled' },
      active: { text: 'Active', class: 'status-active' },
      completed: { text: 'Completed', class: 'status-completed' },
      cancelled: { text: 'Cancelled', class: 'status-cancelled' }
    };
    return badges[status] || { text: status, class: '' };
  };

  const hasFollowUps = (interview) => {
    return interview.questions?.some(q => q.enableFollowUp === true);
  };

  const hasNestedFollowUps = (interview) => {
    return interview.questions?.some(q => q.enableNestedFollowUp === true);
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>Interviews</h2>
            <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
              Manage and track interview schedules
            </p>
          </div>
          <button onClick={() => navigate('/schedule-interview')} className="btn btn-primary">
            + Schedule Interview
          </button>
        </div>

        {/* Filters Row 1 */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Search interviews..."
            style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
          />
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', minWidth: '150px' }}
          >
            <option value="">All Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Filters Row 2 */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
          <select
            name="groupId"
            value={filters.groupId}
            onChange={handleFilterChange}
            style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
          >
            <option value="">All Groups</option>
            {groups.map(group => (
              <option key={group._id} value={group._id}>
                {group.name} ({group.students?.length || 0} students)
              </option>
            ))}
          </select>
          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
            placeholder="Start Date"
            style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
          />
          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
            placeholder="End Date"
            style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}
          />
          {(filters.groupId || filters.startDate || filters.endDate) && (
            <button
              onClick={() => setFilters(prev => ({ ...prev, groupId: '', startDate: '', endDate: '' }))}
              style={{
                padding: '8px 16px',
                background: '#f7fafc',
                color: '#718096',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Clear Filters
            </button>
          )}
        </div>

      {loading ? (
        <div className="loading">Loading interviews...</div>
      ) : interviews.length === 0 ? (
        <div className="no-interviews">
          <p>No interviews found.</p>
        </div>
      ) : (
        <>
          <div className="interviews-list">
            {interviews.map(interview => (
                <div key={interview._id} style={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h3 style={{ margin: 0, color: '#2d3748', fontSize: '18px' }}>{interview.title}</h3>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {hasFollowUps(interview) && (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: '#e9d8fd',
                          color: '#6b46c1'
                        }}>
                          🤖 Follow-ups
                        </span>
                      )}
                      {hasNestedFollowUps(interview) && (
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: '#fed7e2',
                          color: '#97266d'
                        }}>
                          🔄 Nested
                        </span>
                      )}
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: interview.requireVideoRecording !== false ? '#dbeafe' : '#f3f4f6',
                        color: interview.requireVideoRecording !== false ? '#1e40af' : '#6b7280'
                      }}>
                        {interview.requireVideoRecording !== false ? '📹 Video' : '📝 No Video'}
                      </span>
                      <span style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: getStatusBadge(interview.status).class === 'status-scheduled' ? '#e7f3ff' :
                                  getStatusBadge(interview.status).class === 'status-active' ? '#c6f6d5' :
                                  getStatusBadge(interview.status).class === 'status-completed' ? '#e2e8f0' : '#fed7d7',
                        color: getStatusBadge(interview.status).class === 'status-scheduled' ? '#0066cc' :
                              getStatusBadge(interview.status).class === 'status-active' ? '#22543d' :
                              getStatusBadge(interview.status).class === 'status-completed' ? '#2d3748' : '#742a2a'
                      }}>
                        {getStatusBadge(interview.status).text}
                      </span>
                    </div>
                  </div>

                  {interview.description && (
                    <p style={{ color: '#718096', marginBottom: '16px', lineHeight: 1.5, fontSize: '14px' }}>{interview.description}</p>
                  )}

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '12px',
                    marginBottom: '16px',
                    padding: '16px',
                    background: '#f7fafc',
                    borderRadius: '8px'
                  }}>
                    <div style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#718096', display: 'block', marginBottom: '4px' }}>Start:</strong>
                      <span style={{ color: '#2d3748' }}>{new Date(interview.startDate).toLocaleString()}</span>
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#718096', display: 'block', marginBottom: '4px' }}>End:</strong>
                      <span style={{ color: '#2d3748' }}>{new Date(interview.endDate).toLocaleString()}</span>
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#718096', display: 'block', marginBottom: '4px' }}>Time Remaining:</strong>
                      <span style={{ color: getRemainingDays(interview.endDate).color, fontWeight: 600 }}>
                        {getRemainingDays(interview.endDate).text}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#718096', display: 'block', marginBottom: '4px' }}>Duration:</strong>
                      <span style={{ color: '#2d3748' }}>{interview.duration} minutes</span>
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#718096', display: 'block', marginBottom: '4px' }}>Questions:</strong>
                      <span style={{ color: '#2d3748' }}>{interview.questions?.length || 0}</span>
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      <strong style={{ color: '#718096', display: 'block', marginBottom: '4px' }}>Multiple Takes:</strong>
                      <span style={{ color: '#2d3748' }}>
                        {interview.allowMultipleTakes 
                          ? (interview.maxAttempts === 0 ? 'Unlimited' : `Up to ${interview.maxAttempts}`)
                          : 'No'
                        }
                      </span>
                    </div>
                  </div>

                  {interview.stats && (
                    <div style={{ display: 'flex', gap: '20px', padding: '16px', background: '#f7fafc', borderRadius: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                      <div>
                        <span style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Assigned</span>
                        <span style={{ fontSize: '20px', fontWeight: 700, color: '#2d3748' }}>{interview.stats.totalAssigned}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Submitted</span>
                        <span style={{ fontSize: '20px', fontWeight: 700, color: '#38a169' }}>{interview.stats.submitted}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>In Progress</span>
                        <span style={{ fontSize: '20px', fontWeight: 700, color: '#667eea' }}>{interview.stats.inProgress}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: '11px', color: '#718096', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Not Started</span>
                        <span style={{ fontSize: '20px', fontWeight: 700, color: '#718096' }}>{interview.stats.notStarted}</span>
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => navigate(`/interview/${interview._id}`)}
                      className="btn btn-primary"
                      style={{ flex: 1, minWidth: '120px' }}
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => navigate(`/edit-interview/${interview._id}`)}
                      className="btn btn-secondary"
                      style={{ flex: 1, minWidth: '120px' }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDuplicate(interview._id)}
                      className="btn btn-secondary"
                      style={{ flex: 1, minWidth: '120px' }}
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={() => navigate(`/interview/${interview._id}/responses`)}
                      className="btn btn-secondary"
                      style={{ flex: 1, minWidth: '120px' }}
                    >
                      Responses ({interview.stats?.submitted || 0})
                    </button>
                    <button
                      onClick={() => navigate(`/interview/${interview._id}/leaderboard`)}
                      className="btn btn-secondary"
                      style={{ 
                        flex: 1, 
                        minWidth: '120px',
                        background: '#667eea',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      🏆 Leaderboard
                    </button>
                    <button
                      onClick={() => navigate(`/interview/${interview._id}/statistics`)}
                      className="btn btn-secondary"
                      style={{ 
                        flex: 1, 
                        minWidth: '120px',
                        background: '#38a169',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      📈 Statistics
                    </button>
                    <button
                      onClick={() => handleDelete(interview._id)}
                      className="btn btn-danger"
                      style={{ flex: 1, minWidth: '120px' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginTop: '24px', padding: '20px' }}>
                <button
                  onClick={() => fetchInterviews(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                <span style={{ fontWeight: 600, color: '#2d3748' }}>
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => fetchInterviews(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="btn btn-secondary"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Interviews;
